import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import LoadBlur from 'components/LoadBlur';

import { useRouter } from 'found';
import Error from './Error';
import ForgotPassword from './ForgotPassword';
import Inactive from './Inactive';
import Login from './Login';
import Register from './Register';
import ResetPassword from './ResetPassword';
import TermsOutOfDate from './TermsOutOfDate';
import Verify from './Verify';

const Authenticator = ({
  message: initialMessage = null,
  onAuthenticated = () => {},
  state: initialState = 'login',
  showLoyaltyMessage,
  setShowLoyaltyMessage,
}) => {
  const {
    match: {
      location: {
        pathname,
        origin,
        search,
      },
    },
  } = useRouter();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [state, setState] = useState(null);

  useEffect(() => {
    const p = pathname !== '/' ? pathname : '/discover/events';
    window.location.href = `${process.env.REACT_APP_RIPPLE_LOGIN}?continue=${encodeURIComponent(`${origin}${p}${search ? encodeURIComponent(search) : ''}`)}&clientId=${process.env.WEB_CLIENT_ID}&deviceId=${localStorage?.getItem('rs_device_id')}`;
  }, [origin, pathname]);

  const onForgot = useCallback(
    () => setState('forgotPassword'),
    [],
  );
  const onLogin = useCallback(
    () => setState('login'),
    [],
  );
  const onRegister = useCallback(
    () => setState('register'),
    [],
  );
  const onStateChange = useCallback(
    (nextState, nextMessage = 'Unknown error') => {
      if (nextState === 'error') {
        setLoading(true);
        setMessage(nextMessage);
        setState('error');
      } else if (nextState === 'authenticated') {
        setLoading(true);
        onAuthenticated();
      } else {
        setLoading(false);
        setState(nextState);
      }
    },
    [onAuthenticated],
  );
  useEffect(
    () => {
      setMessage(initialMessage);
      setState(initialState);
    },
    [
      initialMessage,
      initialState,
    ],
  );

  return (
    <>
      {!loading && (
      <LoadBlur loading={loading}>
        {state === 'error' && (
        <Error
            message={message}
            onLogin={onLogin}
        />
        )}
        {state === 'forgotPassword' && (
        <ForgotPassword
            loading={loading}
            onEmail={setEmail}
            onLoading={setLoading}
            onLogin={onLogin}
            onStateChange={onStateChange}
        />
        )}
        {state === 'login' && (
        <Login
            loading={loading}
            onForgot={onForgot}
            onEmail={setEmail}
            onLoading={setLoading}
            onRegister={onRegister}
            onStateChange={onStateChange}
            showLoyaltyMessage={showLoyaltyMessage}
            setShowLoyaltyMessage={setShowLoyaltyMessage}
        />
        )}
        {state === 'inactive' && <Inactive onLogin={onLogin} />}
        {state === 'register' && (
        <Register
            loading={loading}
            onEmail={setEmail}
            onLoading={setLoading}
            onLogin={onLogin}
            onStateChange={onStateChange}
        />
        )}
        {state === 'resetPassword' && (
        <ResetPassword
            loading={loading}
            onLoading={setLoading}
            onLogin={onLogin}
            onStateChange={onStateChange}
        />
        )}
        {state === 'termsOutOfDate' && (
        <TermsOutOfDate
            loading={loading}
            onLoading={setLoading}
            onStateChange={onStateChange}
        />
        )}
        {state === 'verifyForgot' && (
        <Verify
            message={`Please click the link we sent to ${email} to verify your email address. After you do so, you will be be able to reset your password.`}
            onLogin={onLogin}
            onStateChange={onStateChange}
        />
        )}
        {state === 'verifyLogin' && (
        <Verify
            message={`Please click the link we sent to ${email} to verify your email address. After you do so, you will be signed in.`}
            onLogin={onLogin}
            onStateChange={onStateChange}
        />
        )}
        {state === 'verifyRegister' && (
        <Verify
            message={`Please click the link we sent to ${email} to verify your email address. After you do so, your registration will be complete and you will be signed in.`}
            onLogin={onLogin}
            onStateChange={onStateChange}
        />
        )}
      </LoadBlur>
)}
    </>
  );
};

Authenticator.propTypes = {
  message: PropTypes.string,
  onAuthenticated: PropTypes.func.isRequired,
  state: PropTypes.string,
  showLoyaltyMessage: PropTypes.bool.isRequired,
  setShowLoyaltyMessage: PropTypes.func.isRequired,
};

export default Authenticator;
